const libPictView = require('pict-view');

/** @type {any} */
const _ViewConfiguration = require('./panes/HeadlightPanels-Pane-View-Templates.json')['HeadlightPanels-Pane-NavigationPaneHtml-View'];

/** @typedef {import('./HeadlightPanels-View.js').Pict} Pict */

class HeadlightPaneNavigation extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		const tmpOptions = Object.assign({}, _ViewConfiguration, pOptions);
		super(pFable, tmpOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;

		// local copy of the navigation list objects
		this.navigationList = [];
		// local copy of the custom navigation list objects
		this.customNavigationList = [];
		// handle the href vs onclick for custom navigation items
		this.pict.MetaTemplate.addPattern('{~CustomNavItemHandler:', '~}', (pHash, pData, fCallback) =>
		{
			// if the url is set, use that as the href
			if (pData && pData.href)
			{
				return `href="${pData.href}" onclick="_Pict.views.HeadlightPanels.handlePanelToggle('Navigation', 'close');"`; // close the navigation panel after clicking
			}
			// if the navigationHandler is set, use that as the onclick handler
			else if (pData && pData.navigationHandler)
			{
				return `onclick="_Pict.views.HeadlightPanels.handlePanelToggle('Navigation', 'close', ()=>{${pData.navigationHandler}});"`;
			}
			// otherwise, use the default onclick handler to toggle a panel
			else
			{
				return `onclick="_Pict.views.HeadlightPanels.handlePanelToggle('${pData.name}');"`;
			}
		});
	}

	compileNavigation()
	{
		if (this.pict.settings.HeadlightPanels.Configuration.CustomNavigationList)
		{
			this.compileCustomNavigationList();
		}
		else if (this.pict.settings.HeadlightPanels.Configuration.NavigationList)
		{
			this.compileNavigationList();
		}
		else
		{
			this.pict.log.error('No NavigationList or CustomNavigationList found in the configuration, skipping Navigation panel');
			return;
		}
	}

	/**
	 * Compile the navigation list based on the configuration that has been passed in at the start with
	 * the NavigationList property via the HeadlightPanels.Configuration object
	 * @return {void}
	 */
	compileNavigationList()
	{
		// if the navigation list is already populated, skip processing it again (creating duplicates, etc)
		if (this.navigationList.length)
		{
			return;
		}
		// get the navigation list
		const tempNavList = this.pict.settings.HeadlightPanels.Configuration.NavigationList;

		if (!tempNavList || tempNavList.length === 0)
		{
			this.pict.log.error('No NavigationList found in the configuration, skipping Navigation panel');
			return;
		}

		for (let i = 0; i < tempNavList.length; i++)
		{
			// for each item in the list, look up if there's a corresponding panel and add it to the list
			const tempPanel = this.pict.settings.HeadlightPanels.Configuration.PanelList.find((p) => p.name === tempNavList[i].toLowerCase());
			if (tempPanel)
			{
				this.navigationList.push(tempPanel);
			}
			// if the item is a special case, add it to the list
			// TODO: We should expand this to be more dynamic when we find other special cases we want to add
			else if (tempNavList[i] === 'DownloadDocument')
			{
				this.navigationList.push({
					displayName: 'Download PDF',
					iconClass: 'fa-file-pdf',
					hideOniOS: true,
					navigationHandler: '_Pict.views.HeadlightPaneNavigation.handleDocumentDownload(event)',
				});
			}
			else if (tempNavList[i] === 'CloneDataToNewForm')
			{
				this.navigationList.push({
					displayName: 'Clone Data to New Form',
					iconClass: 'fa-copy',
					navigationHandler: '_Pict.views.DocumentCurrent.cloneDataFromForm()',
				});
			}
			else
			{
				this.pict.log.warn(`Panel ${tempNavList[i]} not found in the PanelList, skipping Navigation item`);
			}
		}
		// Secret panel for users with role index 4 or higher
		if (this.pict.AppData?.CurrentUser?.UserRoleIndex >= 4)
		{
			this.pict.log.info('User role allows for custom navigation list, checking for CustomNavigationList');
			this.navigationList.push({
				'location': 'right',
				'name': 'secret',
				'displayName': 'Secrets',
				'iconClass': 'fa-person-to-portal',
				'view': 'HeadlightPaneSecret',
			});
		}

		this.renderAsync(() =>
		{
			this.pict.log.info('Custom panel configuration set. Re-rendering the panels.');
		});
	}

	compileCustomNavigationList()
	{
		// if the navigation list is already populated, skip processing it again (creating duplicates, etc)
		if (this.customNavigationList.length)
		{
			return;
		}

		this.customNavigationList = this.pict.settings.HeadlightPanels.Configuration.CustomNavigationList || [];

		/* Example of a custom navigation list items
		...,
		{
			Hash: 'Locations',
			Name: 'Locations Of Interest',
			Icon: 'fas fa-location-dot',
			Address: '#/Location/List',
			PanelSource: null
		},
		{
			Hash: 'Settings',
			Name: 'Settings',
			Icon: 'fas fa-cog',
			Address: null,
			PanelSource: 'HeadlightPanels-Settings',
		}
		...,
		*/

		const tmpNavList = [];
		for (let i = 0; i < this.customNavigationList.length; i++)
		{
			// for each item in the list, look up if there's a corresponding panel and add it to the list
			if (this.customNavigationList[i].PanelSource)
			{
				const tmpPanel = this.pict.settings.HeadlightPanels.Configuration.PanelList.find((p) => p.name === this.customNavigationList[i].PanelSource.toLowerCase());
				if (tmpPanel)
				{
					tmpNavList.push(tmpPanel);
				}
				else
				{
					this.pict.log.warn(`Panel ${this.customNavigationList[i].PanelSource} not found in the PanelList, skipping Navigation item`);
				}
			}
			// use the configuration passed in to the custom navigation list
			else
			{
				tmpNavList.push({
					name: this.customNavigationList[i].Hash.toLowerCase(),
					displayName: this.customNavigationList[i].Name || 'Custom Panel',
					iconClass: this.customNavigationList[i].Icon || 'fas fa-wrench',
					href: this.customNavigationList[i].Address || null,
					navigationHandler: this.customNavigationList[i].NavigationHandler || null,
					hideOniOS: this.customNavigationList[i].HideOniOS || false,
				});
			}
		}
		// finally, we just replace the navigationList with the custom navigation list and render it
		// TODO: Do this better, this is a bit of a hack bc I don't want to change the templates right now to avoid regressions
		this.navigationList = tmpNavList;

		// Secret panel for users with role index 4 or higher
		if (this.pict.AppData?.CurrentUser?.UserRoleIndex >= 4)
		{
			this.pict.log.info('User role allows for custom navigation list, checking for CustomNavigationList');
			this.navigationList.push({
				'location': 'right',
				'name': 'secret',
				'displayName': 'Secrets',
				'iconClass': 'fa-person-to-portal',
				'view': 'HeadlightPaneSecret',
			});
		}
		this.renderAsync(() =>
		{
			this.pict.log.info('Custom panel configuration set. Re-rendering the panels.');
		});
	}

	/**
	 * Handle the download of the document
	 * @param {Event} event - The event object
	 * @return {void}
	 */
	async handleDocumentDownload(event)
	{
		if (event && event.target instanceof HTMLElement)
		{
			const element = event.target;
			// set the button to be loading
			element.classList.add('is-loading', 'is-dark', 'button');

			// The server re-renders the PDF from the SERVER-PERSISTED FormData,
			// so flush any unsaved in-memory form state first or the download is
			// stale — same root cause as the form-conversion export (PD-8714).
			// Locked documents are read-only and have nothing to save.
			const tmpReportData = this.pict.AppData?.DocumentData?.ReportData;
			if (tmpReportData && !tmpReportData.Locked)
			{
				const tmpSaveError = await this.pict.providers.DocumentProvider.handleSaveAsync(true);
				if (tmpSaveError)
				{
					this.pict.log.error('Failed to save document before download; the downloaded PDF may be out of date.', tmpSaveError);
					this.pict.views.HeadlightPanels.toast({
						message: 'Could not save the latest changes — the downloaded PDF may be out of date.',
						type: 'is-warning',
					});
				}
			}

			// download the pdf
			this.pict.providers.DocumentProvider.handleDocumentDownload(false, null, () =>
			{
				element.classList.remove('is-loading', 'is-dark', 'button');
			});
		}
	}

	handleInspectionPanelToggle(event)
	{
		if (event && event.target instanceof HTMLElement)
		{
			const element = event.target;
			// set the button to be loading
			element.classList.add('is-loading', 'is-dark', 'button');

			// download the pdf
			this.pict.views.HeadlightPanels.handlePanelToggle('Inspection', 'toggle', () =>
			{
				element.classList.remove('is-loading', 'is-dark', 'button');
			});
		}
	}

	/**
	 * Toggle the download dropdown menu using HeadlightModal.
	 * Loads form conversion options on first open.
	 * @param {Event} pEvent - The click event from the download button
	 * @return {void}
	 */
	async toggleDownloadDropdown(pEvent)
	{
		// If the menu is already open, toggle it closed.
		if (this._downloadMenuOpen)
		{
			this.fable.HeadlightModal.dismissAnchoredMenu();
			this._downloadMenuOpen = false;
			return;
		}

		// Show loading state on the button while fetching schemas
		const tmpButton = document.getElementById('HeadlightPanels-NavBar-Download-Button');
		if (!this._FormConversionSchemasLoaded && tmpButton)
		{
			tmpButton.classList.add('is-loading');
			try
			{
				await this._loadFormConversionSchemas();
			}
			finally
			{
				tmpButton.classList.remove('is-loading');
			}
		}

		// Build the menu items
		let tmpMenuItems = /*html*/`
			<a class="panel-block" onclick="_Pict.views.HeadlightPaneNavigation.handleDropdownPDFDownload(event)">
				<span class="panel-icon"><i class="fas fa-fw fa-file-pdf"></i></span>
				Download PDF
			</a>`;

		if (this._FormConversionSchemas && this._FormConversionSchemas.length > 0)
		{
			for (const tmpSchema of this._FormConversionSchemas)
			{
				const tmpTitle = tmpSchema.Title || ('Template ' + tmpSchema.IDManifestSchema);

				// Determine the file type icon from the template Artifact's mime type
				let tmpIconClass = 'fa-file-arrow-down';
				const tmpMime = tmpSchema.TemplateMimeType || '';
				if (tmpMime.includes('pdf'))
				{
					tmpIconClass = 'fa-file-pdf';
				}
				else if (tmpMime.includes('spreadsheet') || tmpMime.includes('excel') || tmpMime.includes('ms-excel'))
				{
					tmpIconClass = 'fa-file-excel';
				}

				tmpMenuItems += /*html*/`
					<a class="panel-block" onclick="_Pict.views.HeadlightPaneNavigation.handleFormConversionDownload(${tmpSchema.IDManifestSchema}, event)">
						<span class="panel-icon"><i class="fas fa-fw ${tmpIconClass}"></i></span>
						${tmpTitle}
					</a>`;
			}
		}

		// Anchor the menu to the download button. PSM's dropdown handles positioning,
		// auto-flip, viewport clamping, and outside-click / Escape dismissal — the anchor
		// is excluded from its outside-click test, so this button's onclick toggles cleanly.
		const tmpDownloadButton = document.getElementById('HeadlightPanels-NavBar-Download-Button');
		this._downloadMenuOpen = true;
		const tmpMenu = this.fable.HeadlightModal.spawnAnchoredMenu(
			tmpDownloadButton,
			/*html*/`<nav class="panel" style="background-color:var(--bulma-scheme-main);">${tmpMenuItems}</nav>`,
			{ position: 'above', align: 'left' });
		if (tmpMenu && typeof tmpMenu.then === 'function')
		{
			tmpMenu.then(() => { this._downloadMenuOpen = false; });
		}
	}

	/**
	 * Load form conversion schemas from the provider and cache them.
	 * @return {Promise<void>}
	 * @private
	 */
	async _loadFormConversionSchemas()
	{
		try
		{
			const tmpReportData = this.pict.AppData?.DocumentData?.ReportData;
			if (!tmpReportData || !tmpReportData.DocumentType || !tmpReportData.IDProject)
			{
				this._FormConversionSchemasLoaded = true;
				this._FormConversionSchemas = [];
				return;
			}

			this._FormConversionSchemas = await this.pict.providers.FormConversionProvider
				.getFormConversionSchemasForDocument(tmpReportData.DocumentType, tmpReportData.IDProject);

			this._FormConversionSchemasLoaded = true;
		}
		catch (pError)
		{
			this.pict.log.error('Error loading form conversion schemas for download dropdown:', pError);
			this._FormConversionSchemas = [];
			this._FormConversionSchemasLoaded = true;
		}
	}

	/**
	 * Handle downloading the standard PDF from the download modal.
	 * @param {Event} pEvent - The click event
	 * @return {void}
	 */
	handleDropdownPDFDownload(pEvent)
	{
		this.fable.HeadlightModal.dismissModals();
		this.handleDocumentDownload(pEvent);
	}

	/**
	 * Handle downloading a filled form conversion template.
	 * @param {number} pIDManifestSchema - The ManifestSchema ID to fill
	 * @param {Event} pEvent - The click event
	 * @return {void}
	 */
	async handleFormConversionDownload(pIDManifestSchema, pEvent)
	{
		this.fable.HeadlightModal.dismissModals();

		const tmpReportData = this.pict.AppData?.DocumentData?.ReportData;
		if (!tmpReportData || !tmpReportData.IDDocument)
		{
			this.pict.log.error('No document data available for form conversion download.');
			return;
		}

		this.pict.views.HeadlightPanels.toast({
			message: 'Generating filled template...',
			type: 'is-success',
		});

		// The fill-<IDManifestSchema> renderer fills the template from the
		// SERVER-PERSISTED FormData, not the in-memory form. If the user just
		// changed an input — e.g. a MaterialsEntitySelector whose dependent
		// fields are computed asynchronously after entity-bundle fetches — the
		// latest values may not have reached the server yet, so the exported
		// file would be stale (PD-8714). Flush the current form state with an
		// awaited save before downloading. Locked documents are read-only and
		// have nothing to save, so skip the save for them.
		if (!tmpReportData.Locked)
		{
			const tmpSaveError = await this.pict.providers.DocumentProvider.handleSaveAsync(true);
			if (tmpSaveError)
			{
				this.pict.log.error('Failed to save document before form conversion download; the exported file may be out of date.', tmpSaveError);
				this.pict.views.HeadlightPanels.toast({
					message: 'Could not save the latest changes — the exported file may be out of date.',
					type: 'is-warning',
				});
			}
		}

		// Create a download link using the fill-<IDManifestSchema> renderer.
		// The fill URL is otherwise byte-for-byte identical on every export, so
		// the browser (or any intermediate HTTP cache) can serve a previously
		// rendered, now-stale PDF even after the document has been re-saved with
		// new values (PD-8714). Append a unique cache-buster so each export is a
		// distinct request and the server re-fills from the latest saved
		// FormData. The extra query parameter is ignored by the renderer.
		const tmpCacheBuster = `${Date.now()}-${this.pict.getUUID()}`;
		const tmpDownloadLink = document.createElement('a');
		tmpDownloadLink.href = `/1.0/Document/${tmpReportData.IDDocument}/Load/fill-${pIDManifestSchema}?cachebuster=${tmpCacheBuster}`;
		tmpDownloadLink.classList.add('is-hidden');
		tmpDownloadLink.download = '';
		document.body.appendChild(tmpDownloadLink);
		tmpDownloadLink.click();
		document.body.removeChild(tmpDownloadLink);
	}
}

module.exports = HeadlightPaneNavigation;

// additional view-specific templates
const viewSpecificTemplates = [
	{
		// TODO: Make this path less brittle, need to understand local scoping variables to templates better
		Hash: 'HeadlightPanels-NavigationPanel-NavList',
		Template: /*html*/`{~TS:HeadlightPanels-NavigationPanel-NavItem:Context[0].navigationList~}`,
	},
	{
		Hash: 'HeadlightPanels-NavigationPanel-NavItem',
		Template: /*html*/`
		<a class="panel-block {~HideOniOS:Record.hideOniOS~}" {~CustomNavItemHandler:Record~}>
			<span class="panel-icon">
				<i class="fas fa-fw {~D:Record.iconClass~}" aria-hidden="true"></i>
			</span>
			{~D:Record.displayName~}
		</a>`,
	},
	// This is an example of a button that is not part of the navigation list that we want to add to the navigation panel
	{
		Hash: 'HeadlightPanels-NavigationPanel-ExtraButton',
		Template: /*html*/`
		<a class="panel-block is-hidden" onclick="_Pict.views.HeadlightPanels.handlePanelToggle('Secret')">
			<span class="panel-icon">
				<i class="fas fa-fw fa-person-to-portal" aria-hidden="true"></i>
			</span>
			Secrets
		</a>`,
	},
];

// append the view-specific templates to the global templates that are generated from the JSON file
_ViewConfiguration.Templates = [ ..._ViewConfiguration.Templates, ...viewSpecificTemplates ];
module.exports.default_configuration = _ViewConfiguration;
